import type { CoinSubscriptionPlan, DefaultPlan } from 'payments/payments-dialog/lib/plans'
import { AvailableCurrencyCode, CoinPack, SubscriptionPlan } from '@repo/graphql-types'

const COINS_25K = 25_000
const COINS_100K = 100_000
const COINS_250K = 250_000
const COINS_400K = 400_000
const COINS_700K = 700_000
const COINS_1M = 1_000_000

export const DEFAULT_COIN_PLAN: CoinSubscriptionPlan = 'coinPack1M'

export const SUBSCRIPTION_TIERS = [
  {
    coins: COINS_25K,
    priceUsd: 399,
    priceUsdVatNL: 483,
    variant: 'default',
    plan: 'coinPack25k',
  },
  {
    coins: COINS_100K,
    priceUsd: 999,
    priceUsdVatNL: 1209,
    variant: 'default',
    plan: 'coinPack100k',
  },
  {
    coins: COINS_250K,
    priceUsd: 1999,
    priceUsdVatNL: 2419,
    variant: 'highlighted',
    plan: 'coinPack250k',
  },
  {
    coins: COINS_400K,
    priceUsd: 2999,
    priceUsdVatNL: 3629,
    variant: 'default',
    plan: 'coinPack400k',
  },
  {
    coins: COINS_700K,
    priceUsd: 4999,
    priceUsdVatNL: 6049,
    variant: 'default',
    plan: 'coinPack700k',
  },
  {
    coins: COINS_1M,
    priceUsd: 6999,
    priceUsdVatNL: 8469,
    variant: 'default',
    plan: 'coinPack1M',
  },
] as const satisfies ReadonlyArray<{
  coins: number
  priceUsd: number
  priceUsdVatNL: number
  variant: string
  plan: CoinSubscriptionPlan
}>

export const ONE_TIME_PACKS = [
  { coinPack: CoinPack.CoinPack25k, coins: COINS_25K, priceKey: 'coinsTopUp25k' as const },
  { coinPack: CoinPack.CoinPack100k, coins: COINS_100K, priceKey: 'coinsTopUp100k' as const },
  { coinPack: CoinPack.CoinPack250k, coins: COINS_250K, priceKey: 'coinsTopUp250k' as const },
  { coinPack: CoinPack.CoinPack1000k, coins: COINS_1M, priceKey: 'coinsTopUp1000k' as const },
] as const

export const PLAN_TO_PRICE: Partial<Record<SubscriptionPlan, number>> = {
  [SubscriptionPlan.CoinPackMonthly25k]: 399,
  [SubscriptionPlan.CoinPackMonthly100k]: 999,
  [SubscriptionPlan.CoinPackMonthly250k]: 1999,
  [SubscriptionPlan.CoinPackMonthly400k]: 2999,
  [SubscriptionPlan.CoinPackMonthly700k]: 4999,
  [SubscriptionPlan.CoinPackMonthly1M]: 6999,
}

export const COIN_PLAN_TO_SUBSCRIPTION_PLAN: Record<CoinSubscriptionPlan, SubscriptionPlan> = {
  coinPack25k: SubscriptionPlan.CoinPackMonthly25k,
  coinPack100k: SubscriptionPlan.CoinPackMonthly100k,
  coinPack250k: SubscriptionPlan.CoinPackMonthly250k,
  coinPack400k: SubscriptionPlan.CoinPackMonthly400k,
  coinPack700k: SubscriptionPlan.CoinPackMonthly700k,
  coinPack1M: SubscriptionPlan.CoinPackMonthly1M,
}

export const SUBSCRIPTION_PLAN_TO_COIN_PLAN: Partial<
  Record<SubscriptionPlan, CoinSubscriptionPlan>
> = {
  [SubscriptionPlan.CoinPackMonthly25k]: 'coinPack25k',
  [SubscriptionPlan.CoinPackMonthly100k]: 'coinPack100k',
  [SubscriptionPlan.CoinPackMonthly250k]: 'coinPack250k',
  [SubscriptionPlan.CoinPackMonthly400k]: 'coinPack400k',
  [SubscriptionPlan.CoinPackMonthly700k]: 'coinPack700k',
  [SubscriptionPlan.CoinPackMonthly1M]: 'coinPack1M',
}

export function getOneTimePackPriceCents(
  coinPack: CoinPack,
  prices: {
    coinsTopUp25k: number
    coinsTopUp100k: number
    coinsTopUp250k: number
    coinsTopUp1000k: number
  },
) {
  const pack = ONE_TIME_PACKS.find(entry => entry.coinPack === coinPack)
  if (!pack) {
    return undefined
  }
  return Math.round(prices[pack.priceKey] * 100)
}

export function getTierForCoinPlan(plan: CoinSubscriptionPlan) {
  return SUBSCRIPTION_TIERS.find(t => t.plan === plan)
}

export function getTierForSubscriptionPlan(plan: SubscriptionPlan) {
  const coinPlan = SUBSCRIPTION_PLAN_TO_COIN_PLAN[plan]
  return coinPlan ? getTierForCoinPlan(coinPlan) : undefined
}

export function isCoinPlan(plan: DefaultPlan): plan is CoinSubscriptionPlan {
  return plan in COIN_PLAN_TO_SUBSCRIPTION_PLAN
}

export function toAvailableCurrencyCode(code: string | null | undefined): AvailableCurrencyCode {
  const lower = code?.toLowerCase()
  if (lower === 'eur') {
    return AvailableCurrencyCode.Eur
  }
  if (lower === 'gbp') {
    return AvailableCurrencyCode.Gbp
  }
  return AvailableCurrencyCode.Usd
}
